<template>
  <section class="px-14 mt-2 space-y-5 sm-max:px-5 sm:space-y-8" :id="id">
    <h3 v-if="showTitle" class="pb-10 text-center leading-head  font-ww-medium">
      <strong v-html="title"
        :class="newStyle ? 'text-45 md-max:pb-10' : 'sm-max:text-21 lg:text-45 md:text-32 text-22 md-max:pb-4'"></strong>
    </h3>

    <div v-for="(section, index) in sections" :key="index" class="rounded-lg border bg-greenBg border-greenD"
      :class="index == 0 ? '!mt-0' : ''">
      <div class="grid md:flex md-only:px-2 sm-max:px-4" :class="newStyle ? 'md-max:px-8 us-max:!px-3' : ''">
        <div class="lg:min-w-80 z-[1] md-max:col-span-8 sm-max:p-4 lg:m-7 m-3 md-max:pb-0"
          :class="newStyle ? 'flex relative' : ''">
          <nuxt-img class="md-max:mx-auto"
            :class="newStyle ? 'md-max:absolute md-max:w-[188px] md-max:-left-20 md-max:-top-1 z-50 h-fit' : ''"
            :width="section.imageWidth" :height="section.imageHeight" loading="lazy" format="webp"
            :alt="section.imageAlt" :src="section.imageSrc" sizes="sm:350px md:400px lg:431px"></nuxt-img>

          <div
            :class="newStyle ? 'md-max:!text-3xl md:hidden sm:ml-14 sm:left-0 left-14 pl-4 md-max:mt-4 sm-max:max-w-[210px]' : 'hidden'"
            class="inline-block relative py-2 pr-3 text-xl font-bold text-white capitalize xl:text-3xl md:text-2xl font-ww-bold">
            <span class="relative z-10" :class="newStyle ? 'pl-6 block' : ''" v-html="section.title"></span>
            <div class="absolute bg-greenD lg:left-[-50%] top-0 lg:w-[150%] md:w-full w-full h-full" aria-hidden="true">
            </div>
          </div>
        </div>

        <div :class="newStyle ? 'md-max:mt-6' : ''"
          class="flex flex-col justify-start items-start py-10 w-full text-left md-max:col-span-8 md-max:text-center lg-max:px-6 md-max:pt-2">
          <div :class="newStyle ? 'md-max:!text-3xl md:block hidden' : ''"
            class="inline-block relative py-2 pr-3 text-xl font-bold text-white capitalize xl:text-3xl md:text-2xl font-ww-bold md-max:w-full">
            <span class="relative z-10" v-html="section.title"></span>
            <div class="absolute bg-greenD lg:left-[-50%] top-0 lg:w-[150%] md:w-full w-full h-full" aria-hidden="true">
            </div>
          </div>

          <div class="">
            <div class="mt-3 text-base xl:text-xl md:text-lg lg:pr-16 font-ww-regular"
              :class="newStyle ? 'md-max:!text-xl md-max:text-left md-max:!leading-relaxed' : ''"
              v-html="section.description">
            </div>
          </div>
        </div>
      </div>

      <div class="mx-12 mb-10 bg-white rounded-lg sm:mt-7 sm-max:mx-4" v-if="section.extraSection">
        <div class="items-center px-4 pt-6 pb-12 md-max:px-3 md:grid-cols-12 lg:pr-14">
          <div class="md-max:col-span-12">
            <nuxt-img class="relative mx-auto md-max:mt-0" :class="newStyle ? '!mx-0 max-w-' : ''" width="349"
              height="58" loading="lazy" format="webp" :alt="section.extraSectionImageAlt"
              :src="section.extraSectionImage"></nuxt-img>
          </div>

          <div class="md-max:col-span-12 lg:-ml-0">
            <div
              class="text-base lg:text-21 md:text-lg md-max:text-center md-max:pt-2 lg:pt-2 md:leading-8 font-ww-regular">
              <div class="md-max:leading-5" :class="newStyle ? 'text-xl text-left md-max:leading-normal' : ''"
                v-html="section.extraSectionDescription"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  id: {
    type: String,
    default: 'about',
  },
  title: {
    type: String,
    default: 'Here’s Just a Few of <br><br> The Qualities of This Coffee:',
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
  sections: {
    type: Array,
    default: () => [
      {
        imageAlt: 'PURE INGREDIENT',
        imageSrc: '/assets/images/zoom-in-ingredients.jpg',
        imageWidth: 431,
        imageHeight: 339,
        title: 'One Pure Ingredient',
        description: 'Coffee is delicious, but poor quality coffee can be toxic. You want coffee that’s <strong>clean and pure to ensure there are never any pesticides…</strong>',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'Single Origin',
        imageSrc: '/assets/images/freebag-sect3-img2.png',
        imageWidth: 408,
        imageHeight: 389,
        title: 'Single Origin',
        description: 'Most of the coffee for sale in retail stores and coffee shop is a mix-match of beans from various plants and countries around the world. With blends, you might get beans mixed with pesticide-soaked beans. It’s also possible you can get premium dark roast beans mixed with under-dried, toxin-filled lighter roast beans.<br><br>Single Origin coffee ensures you’re getting what you pay for - and nothing you don’t want in your coffee. <strong>Every bag of this coffee, however, is guaranteed single origin.</strong> Each bag is filled with beans from the same farm, the same plant, and the same roast profile.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'Tested for Mycotoxin',
        imageSrc: '/assets/images/freebag-sect3-img3.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'Tested for Mycotoxin',
        description: 'Mycotoxins are mold toxins that can occur in many substances. Naturally, that means <strong>they can grow inside coffee beans too.</strong> (Unfortunately, they’re far more common than you may think.) Mycotoxins can trigger anything from painful, full-body-inflammation to debilitating Chronic Fatigue Syndrome. Several studies have also strongly linked mycotoxins with kidney diseases.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'Tested for Mycotoxin',
        imageSrc: '/assets/images/freebag-sect3-img4.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'Tested for Mycotoxin',
        description: 'For the best-tasting coffee, you don’t want the sun beating down on the beans harming the natural maturation and rich, complex flavor. Only <strong>2% of the world’s coffee is shade-grown.</strong> With shade-grown coffee, no pesticides are needed. You deserve natural, unharmed, pure coffee.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      },
      {
        imageAlt: 'Shade Grown',
        imageSrc: '/assets/images/freebag-sect3-img5.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'Shade Grown',
        description: 'One of the biggest complaints about coffee is the digestive discomfort from some brews. You probably know all about it yourself… the heartburn, indigestion, bathroom trips, and more. <strong>Sometimes you just don’t know what a given coffee is going to do to your stomach!</strong><br><br> Now, a lot of that discomfort has to do with the acid in your stomach. And you may also know the discomfort depends on the type of roast you drink. <strong>(Hint: Medium and Dark Roasts are easier on the stomach!)</strong> So it can be a gamble as to how your stomach will handle a new coffee.<br><br> But because of the steps you’ve just read about above, Lifeboost Coffee guarantees its coffee won’t bother your stomach! There’s nothing inside that can trigger excess stomach acid or affect digestion.<br><br> <strong>Light, medium, dark or espresso, you choose your beans and your stomach is happy.</strong>',
        extraSection: true,
        extraSectionImage: '/assets/images/single-origin-new1.png',
        extraSectionImageAlt: 'Additional Stomach-Friendly Image',
        extraSectionDescription: 'On top of low acidity, Lifeboost Coffee has none of the junk swimming around in most coffees that aggravates your stomach. That\'s why we test for <strong> pesticides, chemicals, GMOs, or mycotoxins.</strong> <br><br> That means nothing that careless farmers and profit-hungry manufacturers use to get their brews out the door fast.'
      },
      {
        imageAlt: 'Tested for Mycotoxin',
        imageSrc: '/assets/images/freebag-sect3-img1.png',
        imageWidth: 389,
        imageHeight: 370,
        title: 'PURE, CLEAN INGREDIENTS',
        description: 'Our coffee contains only one, simple, pure ingredient. You’re getting the cleanest coffee available directly from the highest quality farms located in a nationally-protected area of Central America. The coffee is then carefully roasted according to exact specifications to produce delicious, low-acid coffee you’ll love.',
        extraSection: false,
        extraSectionImage: '',
        extraSectionImageAlt: '',
        extraSectionDescription: ''
      }
    ],
  },
  newStyle: {
    type: Boolean,
    default: false
  }
})

defineBuilderComponent({
  friendlyName: "TitleBlocksImageInfo",
  canHaveChildren: false,
  inputs: [
    {
      name: 'id',
      type: 'text',
      defaultValue: 'about',
      friendlyName: 'Section ID'
    },
    {
      name: "title",
      type: "richText",
      defaultValue: "Here’s Just a Few of <br> The Qualities of This Coffee:",
      friendlyName: "Main Title"
    },
    {
      name: "showTitle",
      type: "boolean",
      defaultValue: true,
      friendlyName: "Show Title"
    },
    {
      name: "sections",
      type: "list",
      friendlyName: "Sections List",
      defaultValue: [
        {
          imageAlt: 'PURE INGREDIENT',
          imageSrc: '/assets/images/zoom-in-ingredients.jpg',
          imageWidth: 431,
          imageHeight: 339,
          title: 'One Pure Ingredient',
          description: 'Coffee is delicious, but poor quality coffee can be toxic. You want coffee that’s <strong>clean and pure to ensure there are never any pesticides…</strong>',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'Single Origin',
          imageSrc: '/assets/images/freebag-sect3-img2.png',
          imageWidth: 408,
          imageHeight: 389,
          title: 'Single Origin',
          description: 'Most of the coffee for sale in retail stores and coffee shop is a mix-match of beans from various plants and countries around the world. With blends, you might get beans mixed with pesticide-soaked beans. It’s also possible you can get premium dark roast beans mixed with under-dried, toxin-filled lighter roast beans.<br><br>Single Origin coffee ensures you’re getting what you pay for - and nothing you don’t want in your coffee. <strong>Every bag of this coffee, however, is guaranteed single origin.</strong> Each bag is filled with beans from the same farm, the same plant, and the same roast profile.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'Tested for Mycotoxin',
          imageSrc: '/assets/images/freebag-sect3-img3.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'Tested for Mycotoxin',
          description: 'Mycotoxins are mold toxins that can occur in many substances. Naturally, that means <strong>they can grow inside coffee beans too.</strong> (Unfortunately, they’re far more common than you may think.) Mycotoxins can trigger anything from painful, full-body-inflammation to debilitating Chronic Fatigue Syndrome. Several studies have also strongly linked mycotoxins with kidney diseases.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'Tested for Mycotoxin',
          imageSrc: '/assets/images/freebag-sect3-img4.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'Tested for Mycotoxin',
          description: 'For the best-tasting coffee, you don’t want the sun beating down on the beans harming the natural maturation and rich, complex flavor. Only <strong>2% of the world’s coffee is shade-grown.</strong> With shade-grown coffee, no pesticides are needed. You deserve natural, unharmed, pure coffee.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        },
        {
          imageAlt: 'Shade Grown',
          imageSrc: '/assets/images/freebag-sect3-img5.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'Shade Grown',
          description: 'One of the biggest complaints about coffee is the digestive discomfort from some brews. You probably know all about it yourself… the heartburn, indigestion, bathroom trips, and more. <strong>Sometimes you just don’t know what a given coffee is going to do to your stomach!</strong><br><br> Now, a lot of that discomfort has to do with the acid in your stomach. And you may also know the discomfort depends on the type of roast you drink. <strong>(Hint: Medium and Dark Roasts are easier on the stomach!)</strong> So it can be a gamble as to how your stomach will handle a new coffee.<br><br> But because of the steps you’ve just read about above, Lifeboost Coffee guarantees its coffee won’t bother your stomach! There’s nothing inside that can trigger excess stomach acid or affect digestion.<br><br> <strong>Light, medium, dark or espresso, you choose your beans and your stomach is happy.</strong>',
          extraSection: true,
          extraSectionImage: '/assets/images/freebag-sect3-4logos.png',
          extraSectionImageAlt: 'Additional Stomach-Friendly Image',
          extraSectionDescription: 'On top of low acidity, Lifeboost Coffee has none of the junk swimming around in most coffees that aggravates your stomach. That\'s why we test for <strong> pesticides, chemicals, GMOs, or mycotoxins.</strong> <br><br> That means nothing that careless farmers and profit-hungry manufacturers use to get their brews out the door fast.'
        },
        {
          imageAlt: 'Tested for Mycotoxin',
          imageSrc: '/assets/images/freebag-sect3-img1.png',
          imageWidth: 389,
          imageHeight: 370,
          title: 'PURE, CLEAN INGREDIENTS',
          description: 'Our coffee contains only one, simple, pure ingredient. You’re getting the cleanest coffee available directly from the highest quality farms located in a nationally-protected area of Central America. The coffee is then carefully roasted according to exact specifications to produce delicious, low-acid coffee you’ll love.',
          extraSection: false,
          extraSectionImage: '',
          extraSectionImageAlt: '',
          extraSectionDescription: ''
        }
      ],
      subFields: [
        {
          name: "imageAlt",
          type: "text",
          friendlyName: "Image Alt Text",
          defaultValue: "Image Alt"
        },
        {
          name: "imageSrc",
          type: "file",
          friendlyName: "Image",
          allowedFileTypes: [
            "jpeg",
            "jpg",
            "png",
            "svg",
            "webp"
          ],
          defaultValue: "/assets/images/default.jpg"
        },
        {
          name: "imageWidth",
          type: "number",
          friendlyName: "Image Width",
          defaultValue: 431
        },
        {
          name: "imageHeight",
          type: "number",
          friendlyName: "Image Height",
          defaultValue: 339
        },
        {
          name: "title",
          type: "richText",
          friendlyName: "Section Title",
          defaultValue: "Section Title"
        },
        {
          name: "description",
          type: "richText",
          friendlyName: "Description",
          defaultValue: "Section Description"
        },
        {
          name: "extraSection",
          type: "boolean",
          friendlyName: "Show Extra Section",
          defaultValue: false
        },
        {
          name: "extraSectionImage",
          type: "file",
          friendlyName: "Extra Section Image",
          allowedFileTypes: [
            "jpeg",
            "jpg",
            "png",
            "svg",
            "webp"
          ],
          defaultValue: "/assets/images/freebag-sect3-4logos.png"
        },
        {
          name: "extraSectionImageAlt",
          type: "text",
          friendlyName: "Extra Section Image Alt",
          defaultValue: "Extra Section Image Alt"
        },
        {
          name: "extraSectionDescription",
          type: "richText",
          friendlyName: "Extra Section Description",
          defaultValue: "Extra Section Description"
        },
      ]
    },
    {
      name: "newStyle",
      type: "boolean",
      defaultValue: false
    }
  ]
});
</script>